<template>
  <div class="row full-width">
    <h6 v-if="showTitle && !customTitle" class="card__subtitle">{{ addressType.name | capitalize }} Address</h6>
    <h6 v-else-if="showTitle && customTitle" class="card__subtitle">
      {{ customTitle }}
    </h6>
    <div v-if="address" class="full-width">
      <template v-for="field in addressFields">
        <div
          v-if="field !== 'address_type' && field !== 'use_address_type'"
          :key="`${addressType.name}_${field}_field`"
          class="col-12 q-mb-md"
        >
          <q-input
            v-if="field !== 'country'"
            :key="`${addressType.name}_${field}`"
            v-model="address[field]"
            :disabled="disableFields"
            :label="getLabel(field)"
            outlined
            @input="notify()"
          />
          <enhanced-q-select
            v-else-if="field === 'country'"
            :id="`${addressType.name}_${field}`"
            :key="`${addressType.name}_${field}`"
            v-model="address.country"
            :disabled="disableFields"
            :options="countries"
            :label="getLabel(field)"
            @input="notify()"
          />
        </div>
      </template>
    </div>
    <div v-if="canCopyAddress" :key="`single_address_${addressType.name}_wrapper`" class="col-12">
      <q-checkbox
        :key="`single_address_${addressType.name}`"
        v-model="singleAddress"
        color="tertiary"
        label="Use the main address"
      />
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapState } from 'vuex'
import hash from 'object-hash'
import EnhancedQSelect from '@/components/general/EnhancedQSelect'

export default {
  name: 'Address',
  components: {
    EnhancedQSelect
  },
  props: {
    addressSetting: {
      type: Object,
      required: true
    },
    canCopyAddress: {
      type: Boolean,
      default: false
    },
    addressToCopy: {
      type: Object,
      default: null
    },
    showTitle: {
      type: Boolean,
      default: false
    },
    customTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      singleAddress: true,
      address: {
        address_1: '',
        address_2: '',
        address_type: null,
        post_code: '',
        city: '',
        state: '',
        country: '',
        use_address_type: null
      },
      manualAddress: {
        address_1: '',
        address_2: '',
        address_type: null,
        post_code: '',
        city: '',
        state: '',
        country: '',
        use_address_type: null
      }
    }
  },
  computed: {
    ...mapState({
      countries: state => state.settings.options.countries
    }),
    addressType() {
      return this.addressSetting.address_type
    },
    addressFields() {
      return Object.keys(this.cleanUpAddressData(cloneDeep(this.addressSetting)))
    },
    disableFields() {
      return this.canCopyAddress && this.singleAddress
    }
  },
  watch: {
    addressSetting(newVal) {
      let addr = this.cleanUpAddressData(cloneDeep(newVal))
      this.singleAddress = !!newVal.use_address_type && this.canCopyAddress
      if (!this.equals(this.address, addr) && !this.singleAddress) {
        this.$set(this, 'address', addr)
        this.$set(this, 'manualAddress', addr)
      }
    },
    addressToCopy() {
      this.copyAddress()
    },
    singleAddress() {
      this.copyAddress()
    }
  },
  created() {
    this.$set(this, 'manualAddress', cloneDeep(this.address))
    this.singleAddress = !!this.addressSetting.use_address_type && this.canCopyAddress
    if (this.singleAddress) {
      let addr = this.cleanUpAddressData(cloneDeep(this.addressToCopy))
      this.$set(this, 'address', addr)
    } else {
      let addr = this.cleanUpAddressData(cloneDeep(this.addressSetting))
      this.$set(this, 'address', addr)
      this.$set(this, 'manualAddress', addr)
    }
  },
  methods: {
    equals(a1, a2) {
      let h1 = hash(a1)
      let h2 = hash(a2)
      return h1 === h2
    },
    getLabel(text) {
      return this.capitalize(text.replace('_', ' '))
    },
    cleanUpAddressData(address) {
      let clean = cloneDeep(address)
      let toClean = ['created_by', 'created_at', 'updated_by', 'updated_at']
      if (clean) {
        for (let prop of toClean) {
          delete clean[prop]
        }
        clean.address_type = this.addressType
      }
      return clean
    },
    copyAddress() {
      let addr =
        this.singleAddress && this.canCopyAddress ? cloneDeep(this.addressToCopy) : cloneDeep(this.manualAddress)
      this.$set(this, 'address', this.cleanUpAddressData(addr))
      this.notify()
    },
    notify() {
      if (!this.singleAddress && this.canCopyAddress) this.$set(this, 'manualAddress', cloneDeep(this.address))
      let addr = cloneDeep(this.address)
      if (this.singleAddress && this.canCopyAddress) addr.use_address_type = this.addressToCopy.address_type
      else addr.use_address_type = null
      this.$emit('address-updated', addr)
    }
  }
}
</script>
